import { ReportDashboardLayout } from '@/Layouts/ReportDashboardLayout'
import VerticallySplitLayout from '@/Layouts/VerticallySplitLayout'
import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import { GFPADetailsChart } from '@/components/GFPA/DetailsReport/GFPADetailsChart'
import { GFPADetailsTable } from '@/components/GFPA/DetailsReport/GFPADetailsTable'
import { GFPATopAndBottomFiveSecuritiesTable } from '@/components/GFPA/DetailsReport/GFPATopAndBottomFiveSecuritiesTable'
import GFPAHeader from '@/components/GFPA/GFPAHeader'
import { GFRDashboardNavigation } from '@/components/GFR/GFRDashboardNavigation'
import {
  gfrColumnFieldForTableChartLinkingValue,
  gfrDetailsDefaultGroupingFields,
  gfrSecurityDetailsDefaultColumnsFieldsToVisible,
  gfrSecurityDetailsTopAndBottom5FieldsToVisible,
  gfrTopAndBottomFiveSecuritiesFilterKey,
} from '@/constants/gfr.constant'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import { IGfrDetailsDTO } from '@/interfaces/gfrReport.interface'
import { IDetailsDataMap } from '@/interfaces/rvDetails.interface'
import { getGFRDetailsReport } from '@/services/axiomReport.service'
import { transformChartData } from '@/utils/chart-utils/chartUtils'
import { getTopAndBottomFiveSecurities } from '@/utils/tableGridUtils'
import { ColDef } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { Grid } from '@mui/material'
import { reportDefaultColDef } from '@utils/grid-utils/colDefs'
import { getTableColumns } from '@utils/grid-utils/columnUtils'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export const GFRDetailsPage: React.FC = () => {
  const selectedReport = useAppSelector((s) => s.axiomReport.selectedReport)
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(true)
  const [gridReference, setGridReference] = useState<AgGridReact>()
  const [securityDetailsTableColumns, setSecurityDetailsTableColumns] =
    useState<ColDef[]>([])
  const [securityDetailsTableData, setSecurityDetailsTableData] = useState<
    IGfrDetailsDTO[]
  >([])
  const [securityDetailsChartData, setSecurityDetailsChartData] =
    useState<IDetailsDataMap>({} as IDetailsDataMap)
  const [
    gfrTopAndBottomFiveSecuritiesColumns,
    setGfrTopAndBottomFiveSecuritiesColumns,
  ] = useState<ColDef[]>([])
  const [gfrTopAndBottomFiveSecurities, setGfrTopAndBottomFiveSecurities] =
    useState<{
      topFiveSecurities: IGfrDetailsDTO[]
      bottomFiveSecurities: IGfrDetailsDTO[]
    }>({ topFiveSecurities: [], bottomFiveSecurities: [] })
  const [expandedRowGroupInformation, setExpandedRowGroupInformation] =
    useState<{
      name: string
      level: number
    }>({ name: '', level: 0 })

  useEffect(() => {
    const fetchSecurityDetails = async () => {
      try {
        setLoading(true)

        const detailsListResponse = await getGFRDetailsReport(
          selectedReport.reportId,
          'Portfolio',
        )
        const gfrDetailsColumnsFields = Object.keys(
          detailsListResponse[0] || {},
        )
        const gfrColumnsMetaData =
          sessionStorage.getItem(AxiomReportType.GFR) &&
          JSON.parse(sessionStorage.getItem(AxiomReportType.GFR) as string)

        const defaultColDef = getTableColumns(
          gfrColumnsMetaData,
          gfrSecurityDetailsDefaultColumnsFieldsToVisible,
          gfrColumnFieldForTableChartLinkingValue,
          gfrDetailsColumnsFields,
          [],
        )

        const gfrTopAndBottomSecuritiesColumns = getTableColumns(
          gfrColumnsMetaData,
          gfrSecurityDetailsTopAndBottom5FieldsToVisible,
          [],
          gfrDetailsColumnsFields,
          [],
          { ...reportDefaultColDef, filter: false },
        )
        const gfrTopAndBottomFiveSecuritiesData = getTopAndBottomFiveSecurities(
          detailsListResponse,
          gfrTopAndBottomFiveSecuritiesFilterKey,
        ) as {
          topFiveSecurities: IGfrDetailsDTO[]
          bottomFiveSecurities: IGfrDetailsDTO[]
        }
        const finalColDefs = [...defaultColDef]
        setSecurityDetailsTableColumns(finalColDefs)
        const chartTransformData = transformChartData(
          detailsListResponse,
          gfrDetailsDefaultGroupingFields,
          gfrColumnFieldForTableChartLinkingValue[0],
          'Notional Weight',
        )
        setSecurityDetailsTableData(detailsListResponse)
        setSecurityDetailsChartData(chartTransformData)
        setGfrTopAndBottomFiveSecuritiesColumns(
          gfrTopAndBottomSecuritiesColumns,
        )
        setGfrTopAndBottomFiveSecurities(gfrTopAndBottomFiveSecuritiesData)
      } catch (err) {
        console.warn(err)
      } finally {
        setLoading(false)
      }
    }

    if (Object.keys(selectedReport).length) {
      fetchSecurityDetails()
    } else {
      navigate('/reports')
    }
  }, [selectedReport])

  const handleExpandRowGroup = (info: { name: string; level: number }) => {
    setExpandedRowGroupInformation((prev) => ({
      ...prev,
      name: info.name,
      level: info.level,
    }))
  }
  const handleColumnRowGroupSelectionChanged = (
    groupColumnFieldName: string,
  ) => {
    const columnGroupName = groupColumnFieldName ? [groupColumnFieldName] : []
    const chartTransformedData = transformChartData(
      securityDetailsTableData,
      columnGroupName,
      gfrColumnFieldForTableChartLinkingValue[0],
      'Notional Weight',
    )
    setSecurityDetailsChartData(chartTransformedData)
  }
  return (
    <ReportDashboardLayout
      selectedReport={selectedReport}
      navigation={<GFRDashboardNavigation />}
    >
      {loading ? (
        <LoadingCard />
      ) : (
        <VerticallySplitLayout
          contentTop={
            securityDetailsChartData.seriesData &&
            securityDetailsChartData.seriesData.length &&
            securityDetailsChartData.drilldownData &&
            securityDetailsChartData.drilldownData.length ? (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <GFPADetailsChart
                    data={securityDetailsChartData}
                    expandedRowGroupInfo={expandedRowGroupInformation}
                    reportType={selectedReport.reportType}
                    gfpaSummarySelectedSeriesName={'Portfolio'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <GFPATopAndBottomFiveSecuritiesTable
                    securitiesDetailsColDef={
                      gfrTopAndBottomFiveSecuritiesColumns
                    }
                    securitiesData={gfrTopAndBottomFiveSecurities}
                    reportType={selectedReport.reportType}
                  />
                </Grid>
              </Grid>
            ) : (
              <NoDataToDisplayCard />
            )
          }
          contentBottom={
            securityDetailsTableData.length ? (
              <GFPADetailsTable
                reportType={selectedReport.reportType}
                rowData={securityDetailsTableData}
                setDetailsTableColumns={setSecurityDetailsTableColumns}
                setGridReference={setGridReference}
                detailsColDef={securityDetailsTableColumns}
                onExpandRowGroup={handleExpandRowGroup}
                onColumnRowGroupSelectionChanged={
                  handleColumnRowGroupSelectionChanged
                }
              />
            ) : (
              <NoDataToDisplayCard />
            )
          }
          headerBottom={
            <GFPAHeader
              reportData={selectedReport}
              gridReference={gridReference as AgGridReact}
            />
          }
        />
      )}
    </ReportDashboardLayout>
  )
}
